import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ARTIFACT_TYPES, ARTIFACT_TYPE_LABELS, type ArtifactType } from '@thottbot/shared';
import { apiGet } from '../../api/client';
import { relativeTime } from '../../util/time';

interface OverviewData {
  artifacts: Record<ArtifactType, number>;
  screenshots: number;
  comments: number;
  pendingSubmissions: number;
  openReports: number;
  users: number;
  lastSeedRun: { id: string; status: string; startedAt: string; finishedAt: string | null } | null;
}

export function Overview() {
  const q = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => apiGet<OverviewData>('/api/admin/overview'),
  });
  if (q.isPending) return <p className="muted">Loading...</p>;
  if (q.isError || !q.data) return <p className="error">Could not load the overview.</p>;
  const d = q.data;
  return (
    <div data-testid="overview">
      <h2>Site totals</h2>
      <div className="table-scroll">
        <table className="grid fields">
          <tbody>
            {ARTIFACT_TYPES.map((t) => (
              <tr key={t}>
                <th>{ARTIFACT_TYPE_LABELS[t].plural}</th>
                <td>{d.artifacts[t].toLocaleString()}</td>
              </tr>
            ))}
            <tr>
              <th>Screenshots</th>
              <td>{d.screenshots.toLocaleString()}</td>
            </tr>
            <tr>
              <th>Comments</th>
              <td>{d.comments.toLocaleString()}</td>
            </tr>
            <tr>
              <th>Users</th>
              <td>{d.users.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <h2>Work to do</h2>
      <ul>
        <li>
          <Link to="/mod">{d.pendingSubmissions} pending submissions</Link>
        </li>
        <li>
          <Link to="/admin/content">{d.openReports} open reports</Link>
        </li>
        <li>
          Last seed run:{' '}
          {d.lastSeedRun ? (
            <Link to="/admin/seed">
              {d.lastSeedRun.status} · {relativeTime(d.lastSeedRun.startedAt)}
            </Link>
          ) : (
            <Link to="/admin/seed">never</Link>
          )}
        </li>
      </ul>
    </div>
  );
}
